import React, { useState, useEffect, Fragment, useContext } from 'react';
import { useParams, useHistory, Redirect, Link } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { Routes } from 'common/enums';
import { AppTranslationHelper } from 'common/types';
import { setDocumentTitle } from 'common/utils';
import { LoadScreen } from 'common/components';
import { ArrowLeftTwoTone } from 'common/icons';
import { CommonLayout } from '../commonLayout/commonLayout';
import styles from './userEditor.module.css';
import { toast } from 'react-toastify';
import { registerUser, updateUser, changePassword } from 'services/userApi';
import { appContext } from 'common/contexts';
import { AppState } from 'common/types';

type UserEditorProps = {
  context: AppState;
  translation: AppTranslationHelper;
  createMode?: boolean;
};

interface UserFormData {
  email: string;
  displayName: string;
  password: string;
  confirmPassword: string;
  role: string;
  parentId: string;
  changePassword?: boolean;
}

const UserEditor: React.FC<UserEditorProps> = ({ context, translation, createMode = false }) => {
  const { t } = translation;
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [redirect, setRedirect] = useState('');
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    displayName: '',
    password: '',
    confirmPassword: '',
    role: 'user',
    parentId: '',
    changePassword: false
  });

  useEffect(() => {
    const title = [t('admin.title'), createMode ? t('admin.users.createUser') : t('admin.users.editUser')];
    setDocumentTitle(title);
  }, [t, createMode]);

  // Variable para determinar si el usuario que se está editando es admin
  const [isEditingAdmin, setIsEditingAdmin] = useState(false);

  useEffect(() => {
    // Solo buscar usuario existente si no estamos en modo de creación
    if (!createMode) {
      setLoading(true);
      const user = context.users.find((user) => user.id === id);
      if (user) {
        // Determinar si el usuario que se está editando es admin
        const isAdmin = user.role === 'admin';
        setIsEditingAdmin(isAdmin);
        
        setFormData({
          email: user.email,
          displayName: user.displayName,
          password: '',
          confirmPassword: '',
          role: user.role,
          parentId: user.parentId,
          changePassword: false
        });
      } else {
        setRedirect(Routes.AdminUsersList);
      }
      setLoading(false);
    }
  }, [createMode, id, context.users]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type, checked } = e.target as HTMLInputElement;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if ((createMode || formData.changePassword) && formData.password !== formData.confirmPassword) {
      toast.error(t('common.passwordsDoNotMatch'));
      return;
    }

    try {
      setIsSubmitting(true);

      if (createMode) {
        const userData = {
          email: formData.email,
          password: formData.password,
          displayName: formData.displayName,
          role: formData.role,
          parentId: context.parentId
        };
       
        await registerUser(userData);
        toast.success(t('admin.users.userCreatedSuccess'));
      } else {
        const updateUserData = {
          displayName: formData.displayName,
          role: formData.role
        };
        
        await updateUser(id, updateUserData);
        
        // Si se marcó la opción de cambiar contraseña, hacer una llamada separada
        if (formData.changePassword && formData.password) {
          await changePassword(id, formData.password);
        }
        
        toast.success(t('admin.users.userUpdatedSuccess'));
      }
      
      setRedirect(Routes.AdminUsersList);
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      toast.error(t('admin.users.errorSavingUser'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <LoadScreen />;
  }

  if (redirect) {
    return <Redirect to={redirect} />;
  }

  // No longer needed as we're using Link directly
  const handleOnClickOnClose = () => {
    history.push(Routes.AdminUsersList);
  };

  const renderHead = (
    <Fragment>
      <Link className="btn btn-link" to={Routes.AdminUsersList}>
        <ArrowLeftTwoTone />
      </Link>
      <div className={styles.title}>
        {createMode ? t('admin.users.createUser') : t('admin.users.editUser')}
      </div>
    </Fragment>
  );

  const renderBody = (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className="mb-4">
        <label htmlFor="email" className="form-label">{t('auth.email')}</label>
        <input
          type="email"
          className="form-control"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          disabled={!createMode}
          required
        />
      </div>

      <div className="mb-4">
        <label htmlFor="displayName" className="form-label">{t('common.name')}</label>
        <input
          type="text"
          className="form-control"
          id="displayName"
          name="displayName"
          value={formData.displayName}
          onChange={handleChange}
        />
      </div>

      {createMode ? (
        <>
          <div className="mb-4">
            <label htmlFor="password" className="form-label">{t('admin.users.password')}</label>
            <input
              type="password"
              className="form-control"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required={createMode}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="confirmPassword" className="form-label">{t('admin.users.confirmPassword')}</label>
            <input
              type="password"
              className="form-control"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required={createMode}
            />
          </div>
        </>
      ) : (
        <>
          <div className="mb-4 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="changePassword"
              name="changePassword"
              checked={formData.changePassword}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="changePassword">
              {t('admin.users.changePassword')}
            </label>
          </div>

          {formData.changePassword && (
            <>
              <div className="mb-4">
                <label htmlFor="password" className="form-label">{t('admin.users.newPassword')}</label>
                <input
                  type="password"
                  className="form-control"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={formData.changePassword}
                />
              </div>

              <div className="mb-4">
                <label htmlFor="confirmPassword" className="form-label">{t('admin.users.confirmNewPassword')}</label>
                <input
                  type="password"
                  className="form-control"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required={formData.changePassword}
                />
              </div>
            </>
          )}
        </>
      )}

      <div className="mb-4">
        <label className="form-label" htmlFor="role">{t('common.role')}</label>
        <select
          className="form-select"
          id="role"
          name="role"
          value={formData.role}
          onChange={handleChange}
          disabled={!createMode && isEditingAdmin}
        >
          <option value="user">{t('admin.users.roleUser')}</option>
          <option value="cashier">{t('admin.users.roleCashier')}</option>
          {formData.role === 'admin' && <option value="admin">Admin</option>}
        </select>
        <div className="form-text text-muted">
          {!createMode && isEditingAdmin 
            ? t('admin.users.adminRoleCannotBeChanged') 
            : t('admin.users.roleDescription')}
        </div>
      </div>

      <div className="mb-4">
        <div className="form-label" />
        <div className={styles.actions}>
          {isSubmitting ? (
            <button
              type="button"
              className="btn btn-secondary"
              disabled
            >
              {t('common.cancel')}
            </button>
          ) : (
            <Link
              to={Routes.AdminUsersList}
              className="btn btn-secondary"
            >
              {t('common.cancel')}
            </Link>
          )}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              </>
            ) : t('common.save')}
          </button>
        </div>
      </div>
    </form>
  );

  return <CommonLayout head={renderHead} body={renderBody} />;
};

export default UserEditor;
